import CardTransactionsTable from 'app/pages/cards/card/CardTransactionsTable';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import useProfilePictureMap from 'app/hooks/useProfilePictureMap';
import BudgetProgressBar from 'app/shared-components/ui/BudgetProgress/BudgetProgressBar';
import useSidesheetById from 'app/hooks/useSidesheetById';
import SideSheetContent from 'app/shared-components/sidesheets/SideSheetContent';
import SideSheetHeader from 'app/shared-components/sidesheets/SideSheetHeader';
import LoadMoreButton from 'app/shared-components/layout/table/LoadMoreButton';
import LoadingOverlay from 'app/shared-components/util/LoadingOverlay';
import ShippingStatus from 'app/pages/cards/card/ShippingStatus';
import SpecificCard from 'app/shared-components/cards/SpecificCard';
import { clearCardData } from 'app/shared-components/cards/SpecificCard/cardUtils';
import DatePicker from 'app/shared-components/layout/table/DatePicker';
import Currency from 'app/shared-components/util/Currency';
import PinSheet from 'app/pages/cards/card/PinSheet';
import { format } from 'date-fns';
import { useUserRole } from 'app/hooks/useUserRole';
import { useTranslation } from 'react-i18next';
import { BillingAddress } from './BillingAddress';
import { sentryMask } from 'sentryReplayPrivacy';
import { SidesheetRoutes, useSidesheet } from 'app/shared-components/sidesheets/SidesheetProvider';
import { selectUserProfile } from 'app/store/userProfileSlice';
import { selectCurrentPaidolUserId, selectUserCompanies } from 'app/store/userCompaniesSlice';
import { selectReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { getHNBusinessAccountHolder } from 'app/services/highnoteAccountHolder.service';
import { useAppDispatch, useAppSelector } from 'app/store';
import { Box, Button, Chip, Grid, Typography } from '@mui/material';
import { ReactComponent as MasterCardLogo } from 'assets/master-card-logo.svg';
import { EditOutlined as EditIcon, Error as ErrorIcon, Lock, LockOpen } from '@mui/icons-material';
import ModalActionsCard, { ModalType } from 'app/shared-components/ModalActionsCard/ModalActionsCard';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormType } from 'app/pages/cards/cards/NewCardSheet/newCardUtils';
import {
  budgetSpendByMonthAndPaymentCard,
  generatePaymentCardClientToken,
  getHighnotePaymentCard,
  getCard,
} from 'app/pages/store/CardDataSlice/cardsThunks';
import {
  resetSpecificCard,
  selectCardsDataSlice,
  selectSpecificCardSlice,
  resetOpenCardDetail,
} from 'app/pages/store/CardDataSlice/cardDataSlice';
import { HighnoteProductType, HNPaymentCardClientTokenPermission, HNPaymentCardStatus, Role } from 'API';
import type { GetCardsPayload } from 'CDK_API';
import {
  getTransactions,
  resetTransactionsSlice,
  selectAllTransactions,
  selectTransactionsSlice,
} from 'app/pages/store/transactionsSlice';
import {
  CardControlsContainer,
  CardDetails,
  CardImageContainer,
  CardUnactivatedMessage,
  SecurityButton,
} from './style';

export interface CardSheetProps {
  onClose: (shouldActivate: boolean) => void;
  onEdit: (paymentCard: GetCardsPayload) => void;
  paymentCardId?: string;
  groupId?: string;
  loadPaymentCards: () => Promise<void>;
}

function CardSheet() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sidesheet = useSidesheetById(SidesheetRoutes.Card);
  const { openSidesheet } = useSidesheet();
  const params: CardSheetProps = sidesheet.params;
  const [token, setToken] = useState('');
  const [spendInMonth, setSpendInMonth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [zipcode, setZipcode] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState<ModalType>(ModalType.lock);
  const [filterDate, setFilterDate] = useState<Date | null>(new Date());
  const [pinPanelOpen, setPinPanelOpen] = useState(false);
  const { integration } = useAppSelector(selectReviewOnboardSlice);
  const { selectedPaidolId, userRole } = useAppSelector(selectUserCompanies);
  const {
    cardProduct,
    formFactor,
    status,
    paymentCardDetails,
    cardUsers,
    balance,
    velocityRule,
    maximumTransactionAmount,
    lastActionByRole,
    lastActionByPaidolUserId,
  } = useAppSelector(selectSpecificCardSlice);
  const { openCardDetail, paymentCardList, userCardList, groupCards } = useAppSelector(selectCardsDataSlice);
  const { isAdministrator, isSecondaryAdmin, isGroupAdmin } = useUserRole();
  const currentPaidolUserId = useAppSelector(selectCurrentPaidolUserId);

  // Find the card from the list to get basic card information
  const cardFromList = useMemo(() => {
    return (
      paymentCardList.find((card) => card.paymentCardId === params.paymentCardId) ||
      userCardList.find((card) => card.paymentCardId === params.paymentCardId) ||
      groupCards.find((card) => card.paymentCardId === params.paymentCardId)
    );
  }, [paymentCardList, userCardList, groupCards, params.paymentCardId]);

  // Enhanced data recovery effect - detects when we have paymentCardId but no data
  useEffect(() => {
    if (
      params.paymentCardId &&
      selectedPaidolId &&
      !cardFromList &&
      !openCardDetail &&
      !loading &&
      integration
    ) {
      // Fetch the card data to populate the table
      dispatch(
        getCard({
          filter: {
            and: [{ or: [{ paymentCardId: { eq: params.paymentCardId } }] }],
            paidolId: { eq: selectedPaidolId },
          },
        })
      );

      // Also fetch the detailed card information
      loadHighnotePaymentCard(params.paymentCardId, selectedPaidolId);
    }
  }, [params.paymentCardId, selectedPaidolId, cardFromList, openCardDetail, loading, integration, dispatch]);

  const transactions = useAppSelector(selectAllTransactions);
  const { profile } = useAppSelector(selectUserProfile);

  const isCardholder = Role.CARDHOLDER === userRole;

  const highnotePaymentCard = paymentCardDetails?.highnotePaymentCard;

  //Associated with highnotePaymentCard isAdminBlocked
  const loadHighnotePaymentCard = useCallback(
    (paymentCardId: string, paidolId: string) => {
      return dispatch(
        getHighnotePaymentCard({
          paymentCardId,
          paidolId,
          yearAndMonth: format(new Date(), 'yyyy-MM'),
        })
      )
        .unwrap()
        .then((response) => {
          const cardBelongsToCurrentUser = profile
            ? response.highnotePaymentCard?.paidolUsers?.items.find(
                (item) => item?.paidolUser?.user?.id === profile.id
              )
            : false;
          // Don't auto-redirect to activation when viewing card in a group context
          // Let the user view the card details first
          if (
            response?.paymentCardDetails?.status === 'ACTIVATION_REQUIRED' &&
            cardBelongsToCurrentUser &&
            !params.groupId
          ) {
            return params?.onClose(true);
          }
        });
    },
    [dispatch, profile, params]
  );

  const isUsersCard =
    isAdministrator || userCardList.some((card) => card.paymentCardId === params.paymentCardId);

  const loadTransactions = useCallback(
    (paymentCardId: string, paidolId: string) =>
      dispatch(
        getTransactions({
          paidolId,
          paymentCardId,
          filterDate: filterDate,
          shouldGetCardDetails: false,
        })
      ),
    [dispatch, filterDate]
  );

  const loadPaymentCardClientToken = useCallback(
    (paymentCardId: string) =>
      dispatch(
        generatePaymentCardClientToken({
          paymentCardId: paymentCardId,
          permissions: [HNPaymentCardClientTokenPermission.READ_RESTRICTED_DETAILS],
        })
      )
        .unwrap()
        .then((response) => setToken(response)),
    [dispatch]
  );

  const loadAccountBusinessHolder = useCallback(
    (paidolId: string) =>
      getHNBusinessAccountHolder(paidolId).then((response) => {
        setZipcode(response?.businessProfile?.billingAddress?.postalCode || '');
      }),
    []
  );

  const initialLoad = useCallback(() => {
    if (params.paymentCardId && selectedPaidolId) {
      setLoading(true);

      // SEE-1223: Secondary admins load card transactions like Primary —
      // SEE-1081 gave them full paidol-wide transaction visibility, so the
      // old SEE-946 skip (and its "backend returns empty" rationale) is stale.
      const promises = [
        () => loadTransactions(params.paymentCardId || '', selectedPaidolId),
        () => loadPaymentCardClientToken(params.paymentCardId || ''),
        () => loadHighnotePaymentCard(params.paymentCardId || '', selectedPaidolId),
        () => loadAccountBusinessHolder(integration?.businessAccountHolderId || ''),
      ];

      Promise.allSettled(promises.map((p) => p())).finally(() => {
        setLoading(false);
      });
    }
  }, [
    integration?.businessAccountHolderId,
    loadAccountBusinessHolder,
    loadHighnotePaymentCard,
    loadPaymentCardClientToken,
    loadTransactions,
    params.paymentCardId,
    selectedPaidolId,
  ]);

  useEffect(() => {
    if (integration) initialLoad();
  }, [dispatch, integration, initialLoad, selectedPaidolId, params.paymentCardId]);

  // Reset card data when paymentCardId changes to prevent stale data
  useEffect(() => {
    if (params.paymentCardId) {
      dispatch(resetOpenCardDetail());
      dispatch(resetSpecificCard());
    }
  }, [dispatch, params.paymentCardId]);

  // Separate cleanup effect that only runs on unmount
  useEffect(() => {
    return () => {
      // Only reset when the component is actually unmounting (sidesheet closed)
      if (!sidesheet.isOpen) {
        dispatch(resetSpecificCard());
        dispatch(resetTransactionsSlice());
        setToken('');
        clearCardData();
      }
    };
  }, []); // Empty dependency array means this only runs on mount/unmount

  // Enhanced data recovery effect - detects when we have paymentCardId but no data
  useEffect(() => {
    const recoverData = async () => {
      if (
        params.paymentCardId &&
        selectedPaidolId &&
        !cardFromList &&
        !openCardDetail &&
        !loading &&
        integration
      ) {
        // Fetch the card data to populate the table
        dispatch(
          getCard({
            filter: {
              and: [{ or: [{ paymentCardId: { eq: params.paymentCardId } }] }],
              paidolId: { eq: selectedPaidolId },
            },
          })
        );

        // Also fetch the detailed card information
        loadHighnotePaymentCard(params.paymentCardId, selectedPaidolId);
      }
    };

    recoverData();
  }, [params.paymentCardId, selectedPaidolId, cardFromList, openCardDetail, loading, integration, dispatch]);

  const handleOpenModal = (type: ModalType) => {
    setModalType(type);
    setOpenModal(true);
  };

  useEffect(() => {
    if (selectedPaidolId && params.paymentCardId) {
      dispatch(
        budgetSpendByMonthAndPaymentCard({
          paidolId: selectedPaidolId,
          paymentCardId: params.paymentCardId,
          yearAndMonth: format(new Date(), 'yyyy-MM'),
        })
      )
        .unwrap()
        .then((response) => {
          setSpendInMonth(response?.amount ?? 0);
        });
    }
  }, [selectedPaidolId, params.paymentCardId, dispatch]);

  const {
    loadingMore,
    nextToken,
    total,
    loading: loadingTransactions,
  } = useAppSelector(selectTransactionsSlice);

  const loadMore = useCallback(() => {
    if (params.paymentCardId) {
      dispatch(
        getTransactions({
          paidolId: selectedPaidolId,
          paymentCardId: params.paymentCardId,
          shouldGetCardDetails: false,
          filterDate: filterDate,
          nextToken,
        })
      );
    }
  }, [params, dispatch, selectedPaidolId, nextToken, filterDate]);

  const cardholderFromList = cardFromList?.cardholder ?? openCardDetail?.cardholder;

  // Add this simple cardholders array with just the single cardholder
  const cardholders = useMemo(() => {
    return cardholderFromList ? [cardholderFromList] : [];
  }, [cardholderFromList]);

  const { CardholderComponent } = useProfilePictureMap(cardholders);

  const cardName = cardFromList?.name ?? highnotePaymentCard?.name ?? cardProduct.name;
  const cardStatus = cardFromList?.status ?? status;
  const cardFormFactor = cardFromList?.formFactor ?? formFactor;
  const cardLast4 = cardFromList?.last4 ?? highnotePaymentCard?.last4;
  const cardGroup = cardFromList?.cardGroup?.name ?? openCardDetail?.cardGroup?.name;

  // Update existing status checks to use cardStatus
  const isCardActive = cardStatus === HNPaymentCardStatus.ACTIVE;
  const isCardCanceled = cardStatus === HNPaymentCardStatus.CLOSED;
  const isCardUnactivated = cardStatus === HNPaymentCardStatus.ACTIVATION_REQUIRED;
  const isCardEditable = isCardActive && !!cardholderFromList;
  // SEE-946: Primary, Secondary, and Group Admins can perform card-management
  // actions. Route through useUserRole composites so the flag-aware fail-open
  // behavior stays consistent with the rest of the matrix wiring.
  const isAdmin = isAdministrator || isSecondaryAdmin || isGroupAdmin;
  // SPE-3648: a SYSTEM (auto-lock) card is never a self-lock, even if the
  // cardholder's id lingers on lastActionByPaidolUserId from a prior manual
  // self-lock. Mirrors the server guard so the cardholder sees the lockout
  // message and a disabled Unlock instead of a failing action.
  const isSelfLocked = !!(
    currentPaidolUserId &&
    lastActionByPaidolUserId &&
    currentPaidolUserId === lastActionByPaidolUserId &&
    lastActionByRole !== 'SYSTEM'
  );
  const cardBelongsToCurrentUser = profile
    ? highnotePaymentCard?.paidolUsers?.items.find((item) => item?.paidolUser?.user?.id === profile.id)
    : false;

  const expirationDateString = cardFromList?.expirationDate ?? highnotePaymentCard?.expirationDate;
  const isExpired = (() => {
    if (isCardCanceled) {
      return false;
    }
    if (!expirationDateString) {
      return false;
    }
    const expirationDate = new Date(expirationDateString);
    const now = new Date();

    if (isNaN(expirationDate.getTime())) {
      return false;
    }
    return expirationDate < now;
  })();

  // The card name (D1) is masked; the status suffix stays visible.
  const getCardTitle = (): React.ReactNode => {
    const maskedCardName = <span {...sentryMask}>{cardName}</span>;
    if (isCardCanceled) {
      return (
        <>
          {maskedCardName} - {t('cardStatus.closed').toUpperCase()}
        </>
      );
    }
    if (isExpired) {
      return (
        <>
          {maskedCardName} - {t('cardStatus.expired').toUpperCase()}
        </>
      );
    }
    return maskedCardName;
  };

  return (
    <>
      <LoadingOverlay isLoading={loading || loadingTransactions} />
      <SideSheetHeader
        title={getCardTitle()}
        onClose={() => params?.onClose(false)}
        ButtonsComponent={
          !loading &&
          !isCardholder &&
          !isExpired &&
          isCardEditable && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => {
                if (cardFromList) {
                  dispatch(resetOpenCardDetail());
                  openSidesheet(SidesheetRoutes.NewCard, {
                    selectedPaymentCard: cardFromList,
                    formType: FormType.UpdateCard,
                    loadPaymentCards: params.loadPaymentCards,
                    onUpdateCard: async () => {
                      try {
                        // Step 1: Refresh the card list (table) - this updates the three-dots edit
                        await params?.loadPaymentCards?.();

                        // Step 2: Refresh the specific card data (CardSheet) - this updates the CardSheet edit
                        if (params?.paymentCardId && selectedPaidolId) {
                          await loadHighnotePaymentCard(params.paymentCardId, selectedPaidolId);

                          // getCard.fulfilled patches the card in paymentCardList in place — no list refetch (would regress SEE-904 bug 2).
                          await dispatch(
                            getCard({
                              filter: {
                                and: [{ or: [{ paymentCardId: { eq: params.paymentCardId } }] }],
                                paidolId: { eq: selectedPaidolId },
                              },
                            })
                          );
                        }
                      } catch (error) {
                        console.error('Error refreshing card data from CardSheet:', error);
                        // Optionally show a user-friendly error message
                      }
                    },
                  });
                }
              }}
            >
              {t('editCard')}
            </Button>
          )
        }
      />
      <SideSheetContent>
        <React.Fragment>
          <Grid
            container
            sx={{
              flexDirection: { sm: 'center', md: 'center', lg: 'flex-start' },
            }}
          >
            {/* Card Info display */}
            <CardDetails>
              {isCardUnactivated && !isExpired ? (
                <CardUnactivatedMessage>
                  <Box sx={{ mx: { xs: 2, sm: 5 }, alignItems: 'center' }}>
                    <Typography variant="body1" fontSize="1.4rem" fontWeight="500">
                      {t('unactivatedCardMessage')}
                    </Typography>
                  </Box>
                  <Chip
                    style={{
                      color: 'white',
                      fontWeight: 500,
                      fontSize: 18,
                    }}
                    icon={<PowerSettingsNewIcon fontSize="medium" />}
                    label={t('unactivated')}
                    color="warning"
                  />
                </CardUnactivatedMessage>
              ) : null}
              <CardImageContainer isCardUnactivated={isCardUnactivated && !isExpired}>
                <SpecificCard
                  isUsersCard={isUsersCard}
                  paymentCardId={params.paymentCardId ?? ''}
                  clientToken={token}
                  zipcode={zipcode}
                  name={cardName}
                  formFactor={cardFormFactor}
                  blurCard={cardStatus === 'SUSPENDED'}
                  last4={cardLast4}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { sm: '300px', md: '400px' },
                    maxWidth: { xs: '350px' },
                  }}
                />

                {/* Security buttons */}
                <CardControlsContainer>
                  <Typography variant="body1" fontSize="1.8rem" fontWeight="500">
                    {t('security')}
                  </Typography>

                  {cardStatus === 'SUSPENDED' ? (
                    <SecurityButton
                      startIcon={<LockOpen />}
                      onClick={() => handleOpenModal(ModalType.unlock)}
                      disabled={
                        isCardCanceled ||
                        (!isAdmin &&
                          !isSelfLocked &&
                          lastActionByRole != null &&
                          ['ADMINISTRATOR', 'GROUP_ADMINISTRATOR', 'SYSTEM'].includes(lastActionByRole)) ||
                        isExpired
                      }
                    >
                      {t('unlockCard')}
                    </SecurityButton>
                  ) : (
                    <SecurityButton
                      startIcon={<Lock />}
                      onClick={() => handleOpenModal(ModalType.lock)}
                      disabled={isCardCanceled || isExpired}
                    >
                      {t('lockCard')}
                    </SecurityButton>
                  )}
                  {cardBelongsToCurrentUser &&
                    integration?.highnoteProduct?.type === HighnoteProductType.DEBIT && (
                      <SecurityButton
                        startIcon={<Lock />}
                        onClick={() => setPinPanelOpen(true)}
                        disabled={isCardCanceled || isExpired}
                      >
                        {t('resetPIN')}
                      </SecurityButton>
                    )}
                  <SecurityButton
                    startIcon={<ErrorIcon />}
                    onClick={() => handleOpenModal(ModalType.cancel)}
                    disabled={isCardCanceled}
                  >
                    {t('cancelCard')}
                  </SecurityButton>
                </CardControlsContainer>
              </CardImageContainer>
            </CardDetails>

            {/* Billing address */}
            <BillingAddress />

            {/* Card User Info */}
            <Box sx={{ pl: 1, pt: { xs: 1 } }}>
              {isCardCanceled ? (
                <Typography textAlign="center" variant="h3" mb={2} color="error.main">
                  {t('canceled')}
                </Typography>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  mb: 2,
                  mt: 1,
                }}
              >
                <MasterCardLogo />
                <Typography variant="h4" {...sentryMask}>
                  {cardName}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight="normal">
                {t('authorizedUser')}
              </Typography>
              {cardholderFromList
                ? CardholderComponent(cardholderFromList)
                : openCardDetail?.cardholder
                ? CardholderComponent(openCardDetail.cardholder)
                : null}
              <Box sx={{ display: 'flex', mt: 3 }}>
                <Typography variant="body1" sx={{ display: 'flex', flexDirection: 'column' }}>
                  {t('group')} <strong>{cardGroup ?? t('noGroup')}</strong>
                </Typography>
              </Box>
            </Box>
            <ShippingStatus paymentCardDetails={paymentCardDetails} />
          </Grid>
          {lastActionByRole &&
            !isSelfLocked &&
            ['ADMINISTRATOR', 'GROUP_ADMINISTRATOR', 'SYSTEM'].includes(lastActionByRole) &&
            cardStatus === HNPaymentCardStatus.SUSPENDED && (
              <Typography variant="body1" sx={{ mt: 5, color: 'orange', fontWeight: 'bold' }}>
                {lastActionByRole === 'SYSTEM'
                  ? t('cardLockout.cardLockoutMessage')
                  : t('cardLockout.adminLockedMessage')}
              </Typography>
            )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 5,
              mt: 8,
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 500 } }}>
              <Typography variant="display" sx={{ mt: 2, mb: 2 }}>
                <strong>
                  <Currency
                    value={balance?.currentBalance?.value ?? 0}
                    currency={balance?.currentBalance?.currencyCode ?? 'USD'}
                  />
                </strong>
              </Typography>
              <Typography variant="body1" fontWeight="normal" sx={{ mt: 1 }}>
                {t('amountSpent')}
              </Typography>
              <BudgetProgressBar
                sx={{
                  mt: 2,
                  mb: 3,
                }}
                spent={{
                  value: balance?.currentBalance?.value ?? 0,
                  currency: balance?.currentBalance?.currencyCode ?? 'USD',
                }}
                budget={{
                  value: velocityRule?.maximumAmount?.value ?? 0,
                  currency: velocityRule?.maximumAmount?.currencyCode ?? 'USD',
                }}
              />
              <Typography
                variant="body1"
                fontWeight="normal"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                }}
              >
                {t('monthlySpendLimit')}
                <strong>
                  {velocityRule && (
                    <Currency
                      value={velocityRule?.maximumAmount?.value ?? 0}
                      currency={velocityRule?.maximumAmount?.currencyCode ?? 'USD'}
                    />
                  )}
                  {!velocityRule && t('noLimit')}
                </strong>
              </Typography>
              <Typography
                variant="body1"
                fontWeight="normal"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  mt: 1,
                }}
              >
                {t('spendRules.maxTransactionAmount')}
                <strong>
                  {maximumTransactionAmount && (
                    <Currency
                      value={maximumTransactionAmount.value ?? 0}
                      currency={maximumTransactionAmount.currencyCode ?? 'USD'}
                    />
                  )}
                  {!maximumTransactionAmount && t('noLimit')}
                </strong>
              </Typography>
            </Box>
          </Box>
          {/* SEE-1223: shown to Secondary admins too — SEE-1081 gave them
              full transaction visibility, so the SEE-946-era hide is stale. */}
          <DatePicker
            initialValue={filterDate}
            minDate={new Date(2023, 0)}
            maxDate={new Date()}
            onAccept={(newDate) => setFilterDate(newDate)}
          />
          <CardTransactionsTable transactions={transactions} loading={loading} cardUsers={cardUsers} />
          <LoadMoreButton
            loadMore={loadMore}
            loading={loadingMore}
            nextToken={total > transactions.length ? nextToken : undefined}
          />
          <ModalActionsCard
            open={openModal}
            onClose={() => setOpenModal(false)}
            type={modalType}
            paymentCardId={params.paymentCardId ?? ''}
            cardName={cardName}
            currentGroup={
              cardFromList?.cardGroup
                ? {
                    id: cardFromList.cardGroup.id,
                    name: cardFromList.cardGroup.name,
                  }
                : undefined
            }
            onSuccess={async () => {
              try {
                // Refresh the card list to update the table
                await params?.loadPaymentCards?.();
                // Refresh local details so the sheet reflects latest status
                if (params?.paymentCardId && selectedPaidolId) {
                  await loadHighnotePaymentCard(params.paymentCardId, selectedPaidolId);
                }
              } catch (err) {
                // Non-blocking: log and continue
                console.error('Failed to refresh card data after action', err);
              }
            }}
          />
        </React.Fragment>
      </SideSheetContent>

      {pinPanelOpen && (
        <PinSheet
          paymentCardId={params.paymentCardId ?? ''}
          open={pinPanelOpen}
          card={highnotePaymentCard}
          cardProduct={cardProduct}
          onClose={() => {
            setPinPanelOpen(false);
          }}
        />
      )}
    </>
  );
}

export default CardSheet;
